import { useContext, useState, FC, useEffect } from 'react';
import GlobalContext from '../../contexts/GlobalContext';
import { Alert, useTheme, Snackbar, Box, Link, Typography, CircularProgress, Stack } from '@mui/material';
import PasscodeInput from '../PasscodeInput/PasscodeInput';
import { IconEnvelope2, IconEnvelope3 } from '../../icons/Icons';
import mixpanel from 'mixpanel-browser';
import { EMixpanelEvent, toasterDurationMilliseconds } from '../../util/constants';
import { LocationDescriptorObject } from 'history';
import { useHistory } from 'react-router-dom';

type TVerifyWithPasscodeProps = {
    email: string;
    onVerifyPasscode: (passcode: string) => Promise<void>;
    onResendPasscode: () => Promise<void>;
    onSuccessRedirect?: Pick<LocationDescriptorObject, 'pathname' | 'state'>;
};

enum ELocalUiState {
    default,
    loading,
    success,
    error,
}

const VerifyWithPasscode: FC<TVerifyWithPasscodeProps> = (props) => {
    const { email, onVerifyPasscode, onResendPasscode, onSuccessRedirect } = props;
    const { fullLoginLink, language, coBranding } = useContext(GlobalContext);
    const [localUiState, setLocalUiState] = useState(ELocalUiState.default);
    const [errorMessage, setErrorMessage] = useState(language.createAccount.passcode.error.generic);
    const [showToaster, setShowToaster] = useState(false);
    const [disablePasscode, setDisablePasscode] = useState(false);
    const [passcodeGuid, setPasscodeGuid] = useState(Math.random());
    const theme = useTheme();
    const history = useHistory();

    const handleSubmitVerify = async (verificationCode: string) => {
        setLocalUiState(ELocalUiState.loading);
        setDisablePasscode(true);
        try {
            setDisablePasscode(true);
            await onVerifyPasscode(verificationCode.toString());
            mixpanel.track(EMixpanelEvent.actionCompleted, { key: 'verify_account' });
            setLocalUiState(ELocalUiState.success);
        } catch (error) {
            setDisablePasscode(false);
            if (error === 400) {
                mixpanel.track(EMixpanelEvent.actionFailed, { key: 'verify_account' });
                setErrorMessage(language.createAccount.passcode.error.invalid);
            } else {
                setErrorMessage(language.createAccount.passcode.error.generic);
            }
            setLocalUiState(ELocalUiState.error);
        }
    };

    const handleResendPasscode = async () => {
        mixpanel.track(EMixpanelEvent.interacted, { key: 'resend_verification' });
        setPasscodeGuid(Math.random());
        try {
            await onResendPasscode();
            setShowToaster(true);
        } catch (error) {
            if (error === 429) {
                setErrorMessage(language.createAccount.passcode.error.locked);
            } else {
                setErrorMessage(language.createAccount.passcode.error.generic);
            }
            setLocalUiState(ELocalUiState.error);
        }
    };

    useEffect(() => {
        mixpanel.track(EMixpanelEvent.viewed, { key: 'verify_account' });
    }, []);

    useEffect(() => {
        if (localUiState === ELocalUiState.success) {
            onSuccessRedirect
                ? history.push({
                      pathname: onSuccessRedirect.pathname,
                      ...(onSuccessRedirect.state && { state: onSuccessRedirect.state }),
                  })
                : window.location.assign(fullLoginLink);
        }
    }, [localUiState]);

    return (
        <>
            <div data-testid="verify-passcode">
                <Box sx={{ textAlign: { lg: 'center' }, my: 2 }}>
                    {coBranding.brandColor ? (
                        <IconEnvelope3 fillColor={theme.palette.primary.main} />
                    ) : (
                        <IconEnvelope2 />
                    )}
                </Box>
                <Typography component={'h1'} variant={'h4'} sx={{ textAlign: { lg: 'center' } }}>
                    {language.createAccount.passcode.header}
                </Typography>
                <Typography component={'p'} variant={'body2'} sx={{ mt: 1, mb: 2, textAlign: { lg: 'center' } }}>
                    {language.createAccount.passcode.desc}{' '}
                    <Typography component={'span'} variant={'body2'} sx={{ fontWeight: 'bold' }}>
                        {email}
                    </Typography>
                </Typography>
                {localUiState === ELocalUiState.error && (
                    <Alert severity="error" sx={{ my: 2 }}>
                        {errorMessage}
                    </Alert>
                )}
                {localUiState === ELocalUiState.loading && (
                    <Stack direction={'row'} sx={{ justifyContent: 'center' }}>
                        <CircularProgress size={20} />
                    </Stack>
                )}
                <form data-testid="verify-form">
                    <Box sx={{ my: 3 }}>
                        <PasscodeInput key={passcodeGuid} onSubmit={handleSubmitVerify} isDisabled={disablePasscode} />
                    </Box>
                </form>
                <Typography variant="body2" textAlign={{ lg: 'center' }}>
                    {language.createAccount.passcode.desc2}
                </Typography>
                <Box sx={{ my: 2 }}>
                    <Link
                        sx={{ typography: 'subtitle2', margin: { lg: '0 auto' }, display: 'block' }}
                        color={theme.palette.text.primary}
                        component={'button'}
                        type="button"
                        onClick={handleResendPasscode}
                    >
                        {language.createAccount.passcode.cta.resendPasscode}
                    </Link>
                </Box>
            </div>
            <Snackbar
                open={showToaster}
                onClose={() => setShowToaster(false)}
                autoHideDuration={toasterDurationMilliseconds}
            >
                <Alert severity="success">{language.verify.sendSuccess}</Alert>
            </Snackbar>
        </>
    );
};

export default VerifyWithPasscode;
